import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounce } from 'ahooks'
import { Alert, Button, Pagination, Radio, Select } from 'antd'
import type { EnterpriseSelection, ServiceEnterpriseDraft } from '@super-mega/server/contracts/order'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseDocumentsEditor, EnterpriseDraftEditor } from './enterprise-draft-editor.tsx'
import { EnterpriseSummary } from './enterprise-summary.tsx'

/** 页面与弹窗共用的内联编辑区，候选资料只在外层保存时提交。 */
export function EnterpriseInlineSelector({
  customerId,
  defaultKind,
  value,
  onChange,
  disabled,
  onReadyChange
}: {
  customerId: number
  defaultKind: ServiceEnterpriseDraft['kind']
  value?: EnterpriseSelection
  onChange: (value: EnterpriseSelection) => void
  disabled?: boolean
  onReadyChange: (ready: boolean) => void
}) {
  const trpc = useTRPC()
  const access = useAccess()
  const [search, setSearch] = useState({ keyword: '', current: 1 })
  const keyword = useDebounce(search.keyword, { wait: 300 })
  const options = useQuery({
    ...trpc.lookup.subjects.queryOptions({
      purpose: 'order.rental.read',
      customerId,
      keyword,
      current: search.current,
      pageSize: 20
    }),
    placeholderData: keepPreviousData
  })
  const subjectId = value?.mode === 'existing' ? value.subjectId : undefined
  const selected = useQuery({
    ...trpc.order.subjects.detail.queryOptions({ subjectId: subjectId ?? 0 }),
    enabled: Boolean(subjectId) && access.can('subject.read')
  })
  const selectedOption = useQuery({
    ...trpc.lookup.subjects.queryOptions({ purpose: 'order.rental.read', customerId, selectedId: subjectId }),
    enabled: Boolean(subjectId)
  })
  const rows = new Map((options.data?.data ?? []).map((row) => [row.id, row]))
  for (const row of selectedOption.data?.data ?? []) {
    if (row.customerId === customerId) rows.set(row.id, row)
  }
  const mode = value?.mode === 'new' ? 'new' : 'existing'
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="m-0 text-sm font-semibold">服务对象</h3>
          <p className="text-content-muted mt-1 mb-0 text-xs">选择客户名下的对象，或新建并补充资料</p>
        </div>
        <Radio.Group
          aria-label="服务对象来源"
          optionType="button"
          value={mode}
          disabled={disabled}
          options={[
            { value: 'existing', label: '选择已有' },
            { value: 'new', label: '新建服务对象', disabled: !access.can('subject.create') }
          ]}
          onChange={(event) => {
            onReadyChange(true)
            if (event.target.value === 'new') {
              onChange({ mode: 'new', draft: { customerId, kind: defaultKind, displayName: '' } })
            } else {
              onChange({ mode: 'none' })
            }
          }}
        />
      </div>
      {value?.mode === 'new' ? (
        <>
          <EnterpriseDraftEditor
            value={value.draft}
            disabled={disabled}
            onReadyChange={onReadyChange}
            onChange={(draft) => onChange({ mode: 'new', draft })}
          />
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label htmlFor={`enterprise-${customerId}`} className="text-sm font-medium">
              选择服务对象
            </label>
            <Select
              id={`enterprise-${customerId}`}
              className="w-full"
              aria-label="服务对象"
              placeholder="搜索客户名下的服务对象"
              value={subjectId}
              disabled={disabled}
              loading={options.isFetching}
              showSearch={{ filterOption: false, onSearch: (text) => setSearch({ keyword: text, current: 1 }) }}
              options={[...rows.values()].map((row) => ({
                value: row.id,
                label: row.name,
                disabled: row.isArchived || row.kind === 'person'
              }))}
              popupRender={(menu) => (
                <>
                  {menu}
                  <div className="p-2">
                    <Pagination
                      size="small"
                      current={search.current}
                      pageSize={20}
                      total={options.data?.total ?? 0}
                      showSizeChanger={false}
                      onChange={(current) => setSearch((previous) => ({ ...previous, current }))}
                    />
                  </div>
                </>
              )}
              onChange={(id: number) => {
                const row = rows.get(id)
                if (row) {
                  onReadyChange(true)
                  onChange({ mode: 'existing', subjectId: id, expectedVersion: row.version })
                }
              }}
            />
            <p className="text-content-muted m-0 text-xs">可输入名称搜索；选定后在下方核对并补充证件资料。</p>
          </div>
          {options.error && (
            <Alert
              type="error"
              title={options.error.message}
              action={<Button onClick={() => void options.refetch()}>重试</Button>}
            />
          )}
          {selected.error && (
            <Alert
              type="error"
              title={selected.error.message}
              action={<Button onClick={() => void selected.refetch()}>重试</Button>}
            />
          )}
          {selected.data && value?.mode === 'existing' && (
            <>
              <section className="border-border bg-page rounded-lg border p-4">
                <h3 className="mt-0 mb-4 text-sm font-semibold">已选对象信息</h3>
                <EnterpriseSummary enterprise={selected.data} />
              </section>
              {selected.data.version !== value.expectedVersion && (
                <Alert type="warning" title="服务对象资料已更新，请重新选择后核对" />
              )}
              {access.can('subject.material.update') && (
                <EnterpriseDocumentsEditor
                  key={subjectId}
                  current={selected.data}
                  value={value.amendment}
                  disabled={disabled || selected.data.kind === 'person' || selected.data.isArchived}
                  onReadyChange={onReadyChange}
                  onChange={(amendment) => onChange({ ...value, amendment })}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
